import type { ReactNode } from 'react'

type ChatPanelProps = {
  open: boolean
  title: string
  subtitle?: string
  onClose: () => void
  /** Global cancel — sends cancel/stop to the assistant (always visible in header). */
  onCancelIntent?: () => void
  cancelIntentDisabled?: boolean
  /** Short visible label on the header control (e.g. Cancel / Annuler). */
  cancelIntentLabel?: string
  cancelIntentAriaLabel?: string
  closeAriaLabel?: string
  children: ReactNode
}

export function ChatPanel(props: ChatPanelProps) {
  const {
    open,
    title,
    subtitle,
    onClose,
    onCancelIntent,
    cancelIntentDisabled,
    cancelIntentLabel,
    cancelIntentAriaLabel,
    closeAriaLabel = 'Close',
    children,
  } = props

  return (
    <section className="vnw-panel" data-open={open} aria-hidden={!open}>
      <header className="vnw-panelHeader">
        <div className="vnw-panelTitleWrap" aria-label="widget title">
          <div className="vnw-panelTitle">{title}</div>
          {subtitle ? <div className="vnw-panelSubtitle">{subtitle}</div> : null}
        </div>
        <div className="vnw-panelHeaderActions">
          {onCancelIntent ? (
            <button
              type="button"
              className="vnw-cancelIntentBtn"
              disabled={cancelIntentDisabled}
              aria-label={cancelIntentAriaLabel || 'Cancel current action'}
              title={cancelIntentAriaLabel || cancelIntentLabel || 'Cancel'}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (cancelIntentDisabled) return
                onCancelIntent()
              }}
            >
              <span className="vnw-cancelIntentBtnIcon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M8 7.5h8a1.5 1.5 0 0 1 1.5 1.5v6a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 15V9A1.5 1.5 0 0 1 8 7.5Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <span className="vnw-cancelIntentBtnLabel">{cancelIntentLabel || 'Cancel'}</span>
            </button>
          ) : null}
          {onCancelIntent ? <span className="vnw-panelHeaderActionDivider" aria-hidden="true" /> : null}
          <button
            type="button"
            className="vnw-closeBtn"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onClose()
            }}
            aria-label={closeAriaLabel}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </header>
      <div
        className="vnw-panelBody"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </section>
  )
}

